import { isOriginInspectionDocument, type DocumentType } from '../document/document-types';
import type { TradeInput } from '../trade/schema';
import { validateTrade } from '../trade/validation';
import { transportFields, transportItemFields } from '../trade/transport-fields';
import { settlementFields, settlementItemFields } from '../trade/settlement-fields';
import { insuranceFields } from '../trade/insurance-fields';
import { originInspectionFieldsFor, originInspectionItemFieldsFor } from '../trade/origin-inspection-fields';
import { salesContractClauses } from '../trade/sales-contract-clauses';
import { supportsDocumentField, canonicalDocumentField, isOptionalCompletionField, isDocumentClarification } from '../document/field-policy';

export type CompletionField = Readonly<{ key: string; path: string; label: string; question: string; manual: boolean }>;
export type DocumentCompletion = Readonly<{ required: readonly CompletionField[]; recommended: readonly CompletionField[] }>;

const labels: Record<string, string> = {
  'buyer.name': '客户名称', 'buyer.attention': '联系人', 'buyer.address': '客户地址', 'buyer.country': '国家',
  'supplier.name': '供应商名称', 'supplier.attention': '供应商联系人', 'supplier.address': '供应商地址', 'supplier.country': '供应商国家',
  currency: '报价币种', items: '商品明细', incoterm: '贸易条款', namedPlace: '指定地点',
  paymentTerms: '付款方式/条件', paymentMethod: '付款方式', validityDate: '报价有效期', deliveryDate: '交货日期',
  deliveryTerms: '交货条款', shippingMethod: '运输方式', shippingMarks: '唛头', bankAccountVersionId: '收款银行',
  freight: '运费', insurance: '保费', otherCharges: '其他费用', discount: '折扣', note: '单据备注',
  authorizedSignature: '授权签名', vessel: '船名', voyageNumber: '航次', portOfLoading: '起运港', portOfDischarge: '目的港',
  total: '报价总额',
  shipper: '发货人', consignee: '收货人', notifyParty: '通知方', freightTerms: '运费条款', etd: '装期 ETD',
  cutOff: '截关期', specialRequirements: '特殊要求', carrierSignature: '承运人签章',
};
const itemLabels: Record<string, string> = {
  name: '商品名', description: '品名规格', model: '型号', unit: '单位', quantity: '数量', unitPrice: '单价',
  freeSampleReason: '零单价原因', remark: '商品行备注', marks: '唛头', packages: '件数/包装',
  grossWeight: '毛重', netWeight: '净重', measurement: '体积', container: '柜型柜量', pictureDataUri: '商品图片',
};

export function completionFieldLabel(path: string, input: TradeInput, type: DocumentType): string {
  const match = /^items\.(\d+)\.(.+)$/.exec(path);
  const field = match?.[2] ?? path;
  if (!match && path.startsWith('items.')) return `商品明细 · ${itemLabels[path.slice(6)] ?? path.slice(6)}`;
  const definitions = match ? [...transportItemFields, ...settlementItemFields, ...(isOriginInspectionDocument(type) ? originInspectionItemFieldsFor(type) : [])]
    : [...transportFields, ...settlementFields, ...insuranceFields, ...salesContractClauses, ...(isOriginInspectionDocument(type) ? originInspectionFieldsFor(type) : [])];
  const label = (match ? itemLabels[field] : labels[field]) ?? definitions.find(entry => entry.field === field)?.label.split(' / ')[0] ?? field;
  if (!match) return field === 'currency' && type !== 'QUOTATION' ? '币种' : label;
  const index = Number(match[1]);
  const name = input.items[index]?.name ?? input.items[index]?.description;
  return `第 ${index + 1} 项${name ? ` ${name}` : ''} · ${label}`;
}

export function completionFieldKey(path: string, input: TradeInput): string {
  const match = /^items\.(\d+)\.(.+)$/.exec(path);
  return match ? `items.${input.items[Number(match[1])]?.id ?? match[1]}.${match[2]}` : path;
}

export function getDocumentCompletion(input: TradeInput, type: DocumentType, skipped: readonly string[] = []): DocumentCompletion {
  const required: CompletionField[] = [];
  const recommended: CompletionField[] = [];
  const seen = new Set<string>();
  for (const issue of validateTrade(input, type)) {
    if (issue.code === 'AMOUNT_INCOMPLETE' && (input.items.length === 0 || input.currency === null || input.items.some(item => item.quantity === null || item.unitPrice === null))) continue;
    if (issue.code === 'PACKAGES_INCOMPLETE' && input.items.some(item => !item.packages)) continue;
    const canonical = canonicalDocumentField(issue.path);
    if (seen.has(issue.path)) continue;
    if (issue.severity !== 'ERROR' && (!supportsDocumentField(type, canonical) || isOptionalCompletionField(canonical))) continue;
    seen.add(issue.path);
    const key = completionFieldKey(issue.path, input);
    const core = issue.severity === 'ERROR'
      || type === 'PURCHASE_ORDER' && ['supplier.name', 'currency', 'items', 'items.name', 'items.quantity', 'items.unit', 'items.unitPrice'].includes(canonical)
      || type === 'SHIPPING_ORDER' && ['vessel', 'portOfLoading', 'portOfDischarge', 'shipper', 'consignee', 'items', 'items.name'].includes(canonical);
    if (!core && skipped.includes(key)) continue;
    const label = completionFieldLabel(issue.path, input, type);
    const manual = issue.path === 'bankAccountVersionId';
    const question = manual ? '请在编辑表单中选择已确认的收款银行。'
      : issue.path === 'total' ? '总额必须大于零，请核对商品数量、单价及费用或折扣。'
      : /[\u3400-\u9fff]/u.test(issue.message) ? issue.message.replace(/\b(packages|grossWeight|netWeight|measurement)\b/g, field => itemLabels[field] ?? field)
      : issue.code === 'QUANTITY_INVALID' ? '请填写大于零的数量。'
      : issue.code === 'UNIT_REQUIRES_INTEGER' ? '单位为 PCS 时，请填写整数数量。'
      : issue.code === 'FREE_SAMPLE_REASON_REQUIRED' ? '单价为零，请说明原因。'
      : issue.path === 'items' ? '请提供至少一项商品或货物的信息。' : `请补充${label}。`;
    (core ? required : recommended).push({ key, path: issue.path, label, question, manual });
  }
  return { required, recommended };
}

export function buildMissingInputTemplate(completion: DocumentCompletion): string {
  const fields = (completion.required.length ? completion.required : completion.recommended).filter(field => !field.manual && field.path !== 'total');
  return fields.length ? `${fields.map(field => `${field.label}：`).join('\n')}\n` : '';
}

export function getFollowupQuestions(missing: unknown, input: TradeInput, type: DocumentType, _skipped: readonly string[] = []): string[] {
  if (!Array.isArray(missing)) return [];
  const questions = missing.flatMap((entry: unknown): string[] => {
    // Legacy free-text questions have no field scope; current validation supplies
    // the missing-field list safely instead of replaying an unrelated AI question.
    if (typeof entry === 'string') return [];
    if (!entry || typeof entry !== 'object') return [];
    const { field, reason, question, kind } = entry as { field?: string; reason?: string; question?: string | null; kind?: string | null };
    if (typeof field !== 'string') return [];
    if (!isDocumentClarification(type, { field, reason, question, kind })) return [];
    const row = /^items\.(\d+)\./.exec(field);
    if (row && !input.items[Number(row[1])]) return [];
    const text = typeof question === 'string' && question.trim() ? question : reason;
    return typeof text === 'string' && text.trim() ? [text] : [];
  });
  return [...new Set(questions)];
}

export function isExplicitOmission(text: string): boolean {
  return /(?:没有|不提供|未提供|无需|不需要|留空|清空|删除|暂不|无(?:需|型号|备注)?(?:[。；，：\s]|$)|\b(?:none|n\/a|not applicable|not available|not provided|leave blank|clear|remove)\b)/iu.test(text);
}

export function getSkippedFields(proposals: readonly Readonly<{ status: string; operationsJson: unknown; appliedOperationsJson?: unknown }>[], input: TradeInput): string[] {
  const skipped = new Set<string>();
  for (const proposal of proposals) {
    const applied = proposal.appliedOperationsJson ?? proposal.operationsJson;
    if (proposal.status !== 'APPLIED' || !Array.isArray(applied)) continue;
    const operations = applied as Array<{ field?: string; itemId?: string | null; newItemKey?: string | null; value?: string | null; source?: { quote?: string } }>;
    for (const operation of operations) {
      if (!operation.field) continue;
      let key = operation.field;
      if (key.startsWith('items.')) {
        const nameOperation = operations.find(entry => entry.newItemKey && entry.newItemKey === operation.newItemKey && entry.field === 'items.name');
        const matches = input.items.filter(item => item.name && item.name === nameOperation?.value);
        const id = operation.itemId ?? (matches.length === 1 ? matches[0].id : input.items.length === 1 ? input.items[0].id : null);
        if (!id) continue;
        key = `items.${id}.${key.slice(6)}`;
      }
      if (operation.value === null && isExplicitOmission(operation.source?.quote ?? '')) skipped.add(key);
      else if (operation.value) skipped.delete(key);
    }
  }
  return [...skipped];
}
